import type {
  BranchMenuItemServiceOverride,
  MenuItem,
  MenuItemAvailabilityException,
  MenuItemAvailabilityRule,
  MenuScheduleException,
  MenuScheduleWindow,
} from '@/lib/types';

// ─── CAT-01 (doc 20 §5.1, R20.1–R20.3): the availability verdict ──────────────
// Availability is the highest-stakes resolver in the product (doc 20 §3.3). It is
// computed ONCE, server-side, and the verdict travels with the item to every
// surface (R20.1) — no surface recomputes it. The reason is declared and the
// precedence is TOTAL (R20.2): when several inputs disagree, the first match in
// the list below wins, so every caller — guest menu, POS, kiosk, AI tools — reads
// the same answer. `until` is populated wherever computable (R20.3) so a
// temporarily unavailable item becomes a return visit ("Back at 5pm") instead of a
// dead end.

export type UnavailableReason =
  | 'item_disabled' // is_available = false
  | 'outside_window' // availability rule (recurring window)
  | 'exception' // one-off exception
  | 'branch_disabled' // per-branch override disabling the item
  | 'branch_closed' // branch operating hours
  | 'sold_out' // 86'd / paused via sell-state
  | 'out_of_stock' // doc 21, where stock-managed
  | 'mode_inactive' // operating mode off (doc 18)
  | 'menu_schedule'; // menu/line × branch × channel schedule (ADR-37 slice 4)

export type AvailabilityVerdict =
  | { available: true }
  | { available: false; reason: UnavailableReason; until?: string | null; detail?: string | null };

export interface AvailabilityInputs {
  item: Partial<MenuItem> | Record<string, unknown>;
  override?: BranchMenuItemServiceOverride | null;
  rules?: MenuItemAvailabilityRule[];
  exceptions?: MenuItemAvailabilityException[];
  timezone?: string;
  referenceDate?: Date;
  /** Branch operating hours for the `branch_closed` reason (R20.2). */
  branchOperating?: { open: string; close: string; timezone?: string } | null;
  /**
   * Pre-computed "is the branch open right now" for branches with multi-shift /
   * overnight hours, which the single-window `branchOperating` form cannot
   * express. Answered by `isBranchOpenNow` from `lib/branch-operating-hours`
   * (which itself reuses this module's zoned clock helpers), passed in so the
   * resolver stays free of that module. `null` means "no branch hours context".
   */
  branchOpenNow?: boolean | null;
  /** Next open instant (ISO) to surface as `until` when `branchOpenNow` is false. */
  branchNextOpenAt?: string | null;
  /** Whether the relevant operating mode is active (doc 18). */
  modeActive?: boolean | null;
  /** Stock state for the `out_of_stock` reason (doc 21). */
  stock?: { managed: boolean; available: number } | null;
  /**
   * ADR-37 slice 4: menu/line × branch × channel schedule windows already
   * filtered to this item's menu/line + branch + channel (null channel rows =
   * apply to all channels). Evaluated as a declared-precedence step between
   * `branch_closed` and the item-level temporal reasons. Empty/absent = no
   * menu schedule layer for this item = behavior unchanged.
   */
  menuScheduleWindows?: MenuScheduleWindow[] | null;
  /** One-off menu-schedule exceptions (holidays/closures) for the same scope. */
  menuScheduleExceptions?: MenuScheduleException[] | null;
  fallbackAvailable?: boolean;
}

// Precedence is declared here, in one place, and enforced by evaluation order.
// Structural reasons precede temporal ones so the guest sees the most fundamental
// cause first (R20.2). menu_schedule sits between the branch gates (branch hours,
// per-branch disable) and the item-level gates (item disabled, item windows,
// stock) — a menu/line schedule gates a whole catalogue entity, so it outranks
// any single item's rules but yields to branch-level reality.
const PRECEDENCE: UnavailableReason[] = [
  'mode_inactive',
  'branch_closed',
  'menu_schedule',
  'branch_disabled',
  'item_disabled',
  'exception',
  'outside_window',
  'sold_out',
  'out_of_stock',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/** Offset (ms) of `timezone` from UTC at the given instant. */
function getZoneOffsetMs(timezone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    // h23 (00–23), NOT `hour12: false`. On Node 20's ICU, `hour12:false` renders
    // midnight as hour "24" under en-US, so `minutes` came out as 1440 and any
    // "open past midnight" window read as closed for the first hour of the day.
    // `hourCycle:'h23'` is unambiguous and does not depend on the ICU version.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(at);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour === '24' ? '0' : map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUTC - at.getTime();
}

/** Convert a wall-clock time (YYYY-MM-DD + HH:mm) in `timezone` to a UTC instant. */
function zonedWallToUtc(dateStr: string, hhmm: string, timezone: string): Date {
  const base = new Date(`${dateStr}T${hhmm}:00`);
  if (Number.isNaN(base.getTime())) return base;
  const offset = getZoneOffsetMs(timezone, base);
  return new Date(base.getTime() - offset);
}

function validIso(value?: string | null): string | null {
  if (!value) return null;
  const t = new Date(value.trim()).getTime();
  return Number.isFinite(t) ? new Date(value.trim()).toISOString() : null;
}

/** Next start time of any active rule, scanning up to 7 days ahead. */
function nextWindowStart(
  rules: MenuItemAvailabilityRule[],
  referenceDate: Date,
  timezone: string,
): string | null {
  const current = zonedParts(referenceDate, timezone);
  for (let d = 0; d < 7; d++) {
    const targetWeekday = (current.weekday + d) % 7;
    const matches = rules.filter((r) => r.is_active && r.day_of_week === targetWeekday);
    for (const r of matches) {
      const start = minutesFromHHmm(r.start_time);
      if (start < 0) continue;
      const targetDate = addDays(current.date, d);
      const until = zonedWallToUtc(targetDate, r.start_time, timezone);
      if (until.getTime() > referenceDate.getTime()) return until.toISOString();
    }
  }
  return null;
}

function isOutsideOperatingHours(
  b: { open: string; close: string; timezone?: string },
  ref: Date,
  tz: string,
): boolean {
  const current = zonedParts(ref, b.timezone || tz || 'UTC');
  const open = minutesFromHHmm(b.open);
  const close = minutesFromHHmm(b.close);
  if (open < 0 || close < 0) return false;
  return !(current.minutes >= open && current.minutes <= close);
}

function nextBranchOpen(
  b: { open: string; close: string; timezone?: string },
  ref: Date,
  tz: string,
): string | null {
  const zone = b.timezone || tz || 'UTC';
  const current = zonedParts(ref, zone);
  const open = minutesFromHHmm(b.open);
  if (open < 0) return null;
  if (current.minutes < open) {
    return zonedWallToUtc(current.date, b.open, zone).toISOString();
  }
  return zonedWallToUtc(addDays(current.date, 1), b.open, zone).toISOString();
}

/** Next start time of any active menu-schedule window, scanning up to 7 days ahead. */
function nextMenuScheduleWindowStart(
  windows: MenuScheduleWindow[],
  referenceDate: Date,
  timezone: string,
): string | null {
  const current = zonedParts(referenceDate, timezone);
  for (let d = 0; d < 7; d++) {
    const targetWeekday = (current.weekday + d) % 7;
    const matches = windows.filter((w) => w.is_active && w.day_of_week === targetWeekday);
    for (const w of matches) {
      const start = minutesFromHHmm(w.start_time);
      if (start < 0) continue;
      const targetDate = addDays(current.date, d);
      const until = zonedWallToUtc(targetDate, w.start_time, timezone);
      if (until.getTime() > referenceDate.getTime()) return until.toISOString();
    }
  }
  return null;
}

/**
 * Does `minutes` (on the day whose weekday is `currentWeekday`) fall inside a
 * menu-schedule window belonging to any of the last 24 hours? Overnight windows
 * (end_time < start_time) wrap past midnight: the window owns [start, 24:00)
 * on its own weekday and [00:00, end] on the next calendar day, so a window
 * declared on Monday 22:00–02:00 still covers Tuesday 01:30. `lead_minutes` is
 * prep/lead time the kitchen needs before the window closes, so ordering
 * effectively stops `lead_minutes` before end_time (an overnight close of 02:00
 * with a 30 min lead stops accepting orders at 01:30 on the wrap day).
 */
function menuWindowCoversMinutes(
  w: MenuScheduleWindow,
  currentWeekday: number,
  minutes: number,
): boolean {
  const start = minutesFromHHmm(w.start_time);
  const end = minutesFromHHmm(w.end_time);
  if (start < 0 || end < 0) return false;
  const effectiveEnd = end - (w.lead_minutes || 0);

  if (w.day_of_week === currentWeekday) {
    if (end >= start) return minutes >= start && minutes <= effectiveEnd;
    // Overnight first half: [start, 24:00) — the lead only trims the morning side.
    return minutes >= start;
  }
  // A window declared for YESTERDAY may wrap into the small hours of today.
  if (w.day_of_week === (currentWeekday + 6) % 7 && end < start && effectiveEnd >= 0) {
    return minutes <= effectiveEnd;
  }
  return false;
}

/**
 * ADR-37 slice 4: does this menu/line × branch × channel schedule currently
 * cover the reference instant, or is the whole catalogue entity outside its
 * windows / temporarily closed by a one-off exception?
 *
 * Returns a closure verdict (already-scoped EXCEPTIONS + WINDOWS must be passed
 * in, pre-filtered to the item's menu/line, branch, and channel — null-channel
 * rows are the "all channels" broadcast). Returns `null` both when there is no
 * schedule at all AND when the schedule is currently open, so a caller that
 * wants "open" state should use the exported boolean helper instead. This shape
 * exists so the dual-purpose `resolveMenuItemServiceState` callers never see a
 * spurious temporal reason.
 */
function evaluateMenuSchedule(
  windows: MenuScheduleWindow[],
  exceptions: MenuScheduleException[],
  timezone: string,
  referenceDate: Date,
): { reason: 'menu_schedule'; until?: string | null } | null {
  if (!windows.length) return null; // no schedule layer = no gate (like empty item rules)
  const zone = windows[0]?.timezone || timezone || 'UTC';
  const current = zonedParts(referenceDate, zone);

  // One-off date override wins over the recurring windows for that date.
  const todayException = exceptions.find((e) => e.exception_date === current.date);
  if (todayException) {
    if (!todayException.is_available) {
      // Closed — all day, or only within the exception's window.
      if (todayException.start_time && todayException.end_time) {
        const s = minutesFromHHmm(todayException.start_time);
        const e = minutesFromHHmm(todayException.end_time);
        if (s >= 0 && e >= 0 && current.minutes >= s && current.minutes <= e) {
          // Closed until the exception window ends; after that the recurring
          // windows take over again.
          return {
            reason: 'menu_schedule',
            until: zonedWallToUtc(current.date, todayException.end_time, zone).toISOString(),
          };
        }
      } else {
        return { reason: 'menu_schedule', until: nextMenuScheduleWindowStart(windows, referenceDate, zone) };
      }
    } else {
      // Forced-open — all day, or only within the exception's window.
      if (todayException.start_time && todayException.end_time) {
        const s = minutesFromHHmm(todayException.start_time);
        const e = minutesFromHHmm(todayException.end_time);
        if (s < 0 || e < 0) return null;
        if (current.minutes < s || current.minutes > e) {
          return {
            reason: 'menu_schedule',
            until: zonedWallToUtc(current.date, todayException.start_time, zone).toISOString(),
          };
        }
        return null; // inside the forced-open window
      }
      return null; // open all day
    }
  }

  const withinAny = windows.some(
    (w) => w.is_active && menuWindowCoversMinutes(w, current.weekday, current.minutes),
  );
  if (!withinAny) {
    return { reason: 'menu_schedule', until: nextMenuScheduleWindowStart(windows, referenceDate, zone) };
  }
  return null;
}

function evaluateTemporal(
  item: Partial<MenuItem> | Record<string, unknown>,
  rules: MenuItemAvailabilityRule[],
  exceptions: MenuItemAvailabilityException[],
  timezone: string,
  referenceDate: Date,
): { reason: 'outside_window' | 'exception'; until?: string | null } | null {
  const current = zonedParts(referenceDate, timezone);
  const matchingException = exceptions.find((e) => e.exception_date === current.date);
  if (matchingException) {
    if (!matchingException.is_available) {
      const until =
        matchingException.start_time && matchingException.end_time
          ? zonedWallToUtc(current.date, matchingException.end_time, timezone).toISOString()
          : null;
      return { reason: 'exception', until };
    }
    if (matchingException.start_time && matchingException.end_time) {
      const start = minutesFromHHmm(matchingException.start_time);
      const end = minutesFromHHmm(matchingException.end_time);
      if (current.minutes < start || current.minutes > end) {
        return {
          reason: 'exception',
          until: zonedWallToUtc(current.date, matchingException.end_time, timezone).toISOString(),
        };
      }
    }
    // Exception grants availability within its window — fall through to rules.
  }

  if (rules.length > 0) {
    const within = isMenuItemAvailableAt({
      rules,
      exceptions: [],
      referenceDate,
      fallbackAvailable: false,
    });
    if (!within) {
      return { reason: 'outside_window', until: nextWindowStart(rules, referenceDate, timezone) };
    }
  }

  if (hasLegacySchedule(item) && !isMenuItemAvailableByLegacySchedule({ item, timezone, referenceDate })) {
    return { reason: 'outside_window', until: null };
  }

  return null;
}

/**
 * CAT-01 canonical resolver. Evaluates the eight availability inputs in the
 * declared, total precedence (R20.2) and returns a verdict with an optional
 * `until` (R20.3). Pure: give it the same inputs and you get the same answer
 * every time, on every surface.
 */
export function resolveAvailabilityVerdict(args: AvailabilityInputs): AvailabilityVerdict {
  const {
    item,
    override,
    rules = [],
    exceptions = [],
    timezone = 'UTC',
    referenceDate = new Date(),
    branchOperating = null,
    branchOpenNow = null,
    branchNextOpenAt = null,
    modeActive = null,
    stock = null,
    menuScheduleWindows = null,
    menuScheduleExceptions = null,
    fallbackAvailable = true,
  } = args;

  const ref = referenceDate;
  const isComingSoon = item.is_coming_soon === true;
  const isItemAvailable = item.is_available !== false;

  // 1. mode_inactive
  if (modeActive === false) {
    return { available: false, reason: 'mode_inactive' };
  }

  // 2. branch_closed — either the single-window `branchOperating` form (used by
  // earlier callers) or the pre-computed multi-shift form `branchOpenNow`
  // (answered by lib/branch-operating-hours for the public menu, R20.2).
  if (branchOperating && isOutsideOperatingHours(branchOperating, ref, timezone)) {
    return { available: false, reason: 'branch_closed', until: nextBranchOpen(branchOperating, ref, timezone) };
  }
  if (branchOpenNow === false) {
    return { available: false, reason: 'branch_closed', until: branchNextOpenAt ?? null };
  }

  // 2b. menu_schedule (ADR-37 slice 4): a whole menu/line × branch × channel
  // window gates every item that belongs to that catalogue entity, so it
  // outranks any per-item rule but yields to branch-level reality (branch
  // hours / branch disable). Evaluated with the windows/exceptions the caller
  // already scoped to this item's menu/line + branch + channel.
  if (menuScheduleWindows && menuScheduleWindows.length > 0) {
    const menuSchedule = evaluateMenuSchedule(menuScheduleWindows, menuScheduleExceptions || [], timezone, ref);
    if (menuSchedule) {
      return { available: false, reason: menuSchedule.reason, until: menuSchedule.until ?? null };
    }
  }

  // 3. branch_disabled (declared; caller supplies when a per-branch disable applies)
  const ov = override && typeof override === 'object' ? override : null;
  if (ov && (ov as { branch_disabled?: boolean }).branch_disabled === true) {
    return { available: false, reason: 'branch_disabled' };
  }

  // 4. item_disabled (coming_soon folds in here as an item-level block)
  if (isComingSoon) {
    return { available: false, reason: 'item_disabled', detail: 'Coming soon' };
  }
  if (!isItemAvailable) {
    return { available: false, reason: 'item_disabled' };
  }

  // 5 + 6. exception, then outside_window
  const temporal = evaluateTemporal(item, rules, exceptions, timezone, ref);
  if (temporal) {
    return { available: false, reason: temporal.reason, until: temporal.until ?? null };
  }

  // 7. sold_out / paused (86'd via sell-state)
  if (ov?.sell_state === 'sold_out') {
    return {
      available: false,
      reason: 'sold_out',
      until: validIso(ov.stop_sell_until),
      detail: ov.reason?.trim() || null,
    };
  }
  if (ov?.sell_state === 'paused') {
    const stop = ov.stop_sell_until?.trim() || null;
    const until = validIso(stop);
    const stillPaused = until === null || new Date(stop as string).getTime() > ref.getTime();
    if (stillPaused) {
      return { available: false, reason: 'sold_out', until, detail: ov.reason?.trim() || null };
    }
  }

  // 8. out_of_stock (doc 21)
  if (stock?.managed && (stock.available ?? 0) <= 0) {
    return { available: false, reason: 'out_of_stock' };
  }

  void fallbackAvailable;
  return { available: true };
}

/** Guest-facing copy for a verdict, per doc 20 §7.1. Null when available. */
export function availabilityReasonCopy(verdict: AvailabilityVerdict): string | null {
  if (verdict.available) return null;
  const until = verdict.until ? formatClockTime(verdict.until) : null;
  switch (verdict.reason) {
    case 'sold_out':
      return 'Sold out today';
    case 'outside_window':
      return until ? `Available from ${until}` : 'Unavailable right now';
    case 'branch_closed':
      return until ? `Kitchen closed — opens ${until}` : 'Kitchen closed';
    case 'out_of_stock':
      return 'Sold out';
    case 'exception':
      return 'Unavailable today';
    case 'item_disabled':
      return 'Not available';
    case 'branch_disabled':
      return 'Not available at this location';
    case 'mode_inactive':
      return 'Not available right now';
    case 'menu_schedule':
      return until ? `Back at ${until}` : 'Unavailable right now';
  }
}

function formatClockTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(t));
}

/** Re-exported for callers that want to assert precedence ordering. */
export { PRECEDENCE };

export function minutesFromHHmm(value: string): number {
  const [hourRaw, minuteRaw] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return -1;
  }

  return hour * 60 + minute;
}

export function zonedParts(date: Date, timezone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // h23 (00–23), NOT `hour12: false`: on Node 20's ICU `hour12:false` renders
    // midnight as hour "24" under en-US, so `minutes` came out 1440 and any
    // "open past midnight" window read as closed for the first hour of the day.
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  return {
    weekday: weekdayMap[map.weekday || 'Sun'] ?? 0,
    date: `${map.year}-${map.month}-${map.day}`,
    minutes: minutesFromHHmm(`${map.hour}:${map.minute}`),
  };
}

const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function readBaseAvailability(item: Partial<MenuItem> | Record<string, unknown>, fallbackAvailable: boolean) {
  if (typeof item.is_available === 'boolean') return item.is_available;
  return fallbackAvailable;
}

function hasLegacySchedule(item: Partial<MenuItem> | Record<string, unknown>) {
  const availableTimes =
    item.available_Times && typeof item.available_Times === 'object' && !Array.isArray(item.available_Times)
      ? (item.available_Times as Record<string, unknown>)
      : {};

  return Boolean(
    item.available_from ||
      item.available_until ||
      (Array.isArray(item.available_Days) && item.available_Days.length > 0) ||
      availableTimes.start ||
      availableTimes.end,
  );
}

export function isMenuItemAvailableByLegacySchedule(args: {
  item: Partial<MenuItem> | Record<string, unknown>;
  timezone?: string;
  referenceDate?: Date;
  fallbackAvailable?: boolean;
}): boolean {
  const {
    item,
    timezone = 'UTC',
    referenceDate = new Date(),
    fallbackAvailable = true,
  } = args;

  const current = zonedParts(referenceDate, timezone);

  if (item.available_from && current.date < String(item.available_from)) {
    return false;
  }

  if (item.available_until && current.date > String(item.available_until)) {
    return false;
  }

  if (Array.isArray(item.available_Days) && item.available_Days.length > 0) {
    const allowedDays = new Set(
      item.available_Days.map((entry) => String(entry || '').trim().toLowerCase()).filter(Boolean),
    );
    const currentDay = WEEKDAY_NAMES[current.weekday] || 'sunday';
    if (!allowedDays.has(currentDay)) {
      return false;
    }
  }

  const availableTimes =
    item.available_Times && typeof item.available_Times === 'object' && !Array.isArray(item.available_Times)
      ? (item.available_Times as Record<string, unknown>)
      : {};

  const start = minutesFromHHmm(String(availableTimes.start || ''));
  const end = minutesFromHHmm(String(availableTimes.end || ''));

  if (start >= 0 && end >= 0) {
    return current.minutes >= start && current.minutes <= end;
  }

  if (start >= 0) {
    return current.minutes >= start;
  }

  if (end >= 0) {
    return current.minutes <= end;
  }

  return fallbackAvailable;
}

export function resolveMenuItemAvailability(args: {
  item: Partial<MenuItem> | Record<string, unknown>;
  rules?: MenuItemAvailabilityRule[];
  exceptions?: MenuItemAvailabilityException[];
  timezone?: string;
  referenceDate?: Date;
  fallbackAvailable?: boolean;
}) {
  const {
    item,
    rules = [],
    exceptions = [],
    timezone = 'UTC',
    referenceDate = new Date(),
    fallbackAvailable = true,
  } = args;

  const baseAvailable = readBaseAvailability(item, fallbackAvailable);
  if (!baseAvailable) {
    return {
      available: false,
      source: 'manual_toggle' as const,
    };
  }

  if (rules.length > 0 || exceptions.length > 0) {
    return {
      available: isMenuItemAvailableAt({
        rules,
        exceptions,
        referenceDate,
        fallbackAvailable: baseAvailable,
      }),
      source: 'advanced_rules' as const,
    };
  }

  if (hasLegacySchedule(item)) {
    return {
      available: isMenuItemAvailableByLegacySchedule({
        item,
        timezone,
        referenceDate,
        fallbackAvailable: baseAvailable,
      }),
      source: 'legacy_schedule' as const,
    };
  }

  return {
    available: baseAvailable,
    source: 'base' as const,
  };
}

function formatRelativePause(until: string) {
  const target = new Date(until);
  const targetMs = target.getTime();
  if (!Number.isFinite(targetMs)) return 'Unavailable temporarily';

  const diffMs = targetMs - Date.now();
  if (diffMs <= 0) return 'Unavailable temporarily';

  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `Unavailable for about ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `Unavailable for about ${hours} hr`;
  const days = Math.round(hours / 24);
  return `Unavailable for about ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Layered branch sharing (doc 38 Part 2): is this (shared/base) item structurally
 * NOT carried by the branch? `is_carried === false` means the branch has excluded
 * it entirely , it should be dropped from the branch's effective catalog, not
 * merely shown as unavailable. Distinct from sold_out/paused (temporary). A
 * missing override, or `is_carried` undefined/true, means carried (the default).
 */
export function isBranchItemExcluded(
  override: BranchMenuItemServiceOverride | null | undefined,
): boolean {
  return override?.is_carried === false;
}

export function resolveMenuItemServiceState(args: {
  item: Partial<MenuItem> | Record<string, unknown>;
  override?: BranchMenuItemServiceOverride | null;
  rules?: MenuItemAvailabilityRule[];
  exceptions?: MenuItemAvailabilityException[];
  timezone?: string;
  referenceDate?: Date;
  fallbackAvailable?: boolean;
  /** Item-level stock state (doc 21) for the `out_of_stock` auto-86. */
  stock?: { managed: boolean; available: number } | null;
}) {
  const {
    item,
    override,
    rules = [],
    exceptions = [],
    timezone = 'UTC',
    referenceDate = new Date(),
    fallbackAvailable = true,
    stock = null,
  } = args;

  // Server-side guard: is_coming_soon is only a UI hint in the template engine,
  // so a customer who knows the item UUID could POST it directly and the
  // availability resolver would not stop it. Block it here at the same layer
  // as sold_out / paused so every ordering path (public API, counter, floor)
  // is covered without each caller needing its own check.
  if (item.is_coming_soon) {
    return {
      available: false,
      orderable: false,
      sellState: 'coming_soon' as const,
      source: 'item_flag' as const,
      label: 'Coming soon',
      reason: null,
    };
  }

  const normalizedOverride =
    override && typeof override === 'object'
      ? override
      : null;

  if (normalizedOverride?.sell_state === 'sold_out') {
    return {
      available: false,
      orderable: false,
      sellState: 'sold_out' as const,
      source: 'branch_override' as const,
      label: normalizedOverride.reason?.trim() || 'Sold out',
      reason: normalizedOverride.reason?.trim() || null,
    };
  }

  if (normalizedOverride?.sell_state === 'paused') {
    const stopSellUntil = normalizedOverride.stop_sell_until?.trim() || null;
    const stillPaused =
      !stopSellUntil || Number.isNaN(new Date(stopSellUntil).getTime()) || new Date(stopSellUntil).getTime() > Date.now();

    if (stillPaused) {
      return {
        available: false,
        orderable: false,
        sellState: 'paused' as const,
        source: 'branch_override' as const,
        label:
          normalizedOverride.reason?.trim() ||
          (stopSellUntil ? formatRelativePause(stopSellUntil) : 'Temporarily unavailable'),
        reason: normalizedOverride.reason?.trim() || null,
      };
    }
  }

  const availability = resolveMenuItemAvailability({
    item,
    rules,
    exceptions,
    timezone,
    referenceDate,
    fallbackAvailable,
  });

  // doc 21: out-of-stock auto-86s the item on the guest menu (R20.1). Mirrors
  // the verdict precedence (sold_out/paused override wins, stock is last).
  if (stock?.managed && (stock.available ?? 0) <= 0) {
    return {
      available: false,
      orderable: false,
      sellState: 'sold_out' as const,
      source: 'stock' as const,
      label: 'Sold out',
      reason: 'out_of_stock' as const,
    };
  }

  return {
    available: availability.available,
    orderable: availability.available,
    sellState: 'available' as const,
    source: availability.source,
    label: availability.available ? null : 'Unavailable right now',
    reason: null,
  };
}

/**
 * The sell-state floor: is this item off the menu right now, regardless of any
 * schedule, stock, or override?
 *
 * This existed as three separate expressions that had already drifted — the cloud
 * till and the kiosk blocked `paused`, but the offline order endpoint did not, so
 * an item a manager had paused kept selling on a till with no connection. Every
 * surface that can take money must answer this question the same way.
 *
 * Deliberately typed against the smallest shape any of them has, so the POS
 * catalogue row, the offline snapshot row, and the guest menu row all fit without
 * a cast. `sell_state` is a plain string here because the offline snapshot is
 * untrusted JSON, not a typed row.
 */
export function isSellStateOffMenu(item: {
  is_available?: boolean | null;
  sell_state?: string | null;
}): boolean {
  if (item.is_available === false) return true;
  return item.sell_state === 'sold_out' || item.sell_state === 'paused';
}

/** Why an item is off the menu, in words a cashier can act on. */
export function sellStateOffMenuReason(item: {
  is_available?: boolean | null;
  sell_state?: string | null;
}): 'sold_out' | 'paused' {
  return item.sell_state === 'paused' ? 'paused' : 'sold_out';
}

export function isMenuItemAvailableAt(args: {
  rules: MenuItemAvailabilityRule[];
  exceptions?: MenuItemAvailabilityException[];
  referenceDate?: Date;
  fallbackAvailable?: boolean;
}): boolean {
  const {
    rules,
    exceptions = [],
    referenceDate = new Date(),
    fallbackAvailable = true,
  } = args;

  const timezone = rules[0]?.timezone || 'UTC';
  const current = zonedParts(referenceDate, timezone);

  // Exception overrides recurring rule behavior.
  const matchingException = exceptions.find((exception) => exception.exception_date === current.date);
  if (matchingException) {
    if (!matchingException.is_available) {
      return false;
    }

    if (matchingException.start_time && matchingException.end_time) {
      const start = minutesFromHHmm(matchingException.start_time);
      const end = minutesFromHHmm(matchingException.end_time);
      return current.minutes >= start && current.minutes <= end;
    }

    return true;
  }

  if (!rules.length) {
    return fallbackAvailable;
  }

  return rules.some((rule) => {
    if (!rule.is_active || rule.day_of_week !== current.weekday) {
      return false;
    }

    const start = minutesFromHHmm(rule.start_time);
    const end = minutesFromHHmm(rule.end_time);
    return current.minutes >= start && current.minutes <= end;
  });
}

/**
 * A row from `menu_item_inventory` (doc 21), the per-branch item-level stock.
 * Recipe-mode items never carry an active row (the `stock_mode` trigger
 * deactivates item-level rows for them), so a `track_inventory !== false` row
 * exists exactly for item-mode items — which is why `managed` can be derived
 * from the row alone.
 */
export type ItemInventoryRow = {
  track_inventory?: boolean | null;
  quantity_on_hand?: number | null;
  is_out_of_stock?: boolean | null;
};

/**
 * Map a branch's item-level inventory rows to the verdict's `stock` input.
 * Pure: no DB, no time. `managed: false` means "not stock-managed here" (the
 * item stays governed by its other availability inputs); `managed: true` with
 * `available: 0` is what the resolver reports as `out_of_stock`.
 */
export function deriveItemStockState(
  rows: ItemInventoryRow[] | undefined | null,
): { managed: boolean; available: number } {
  const active = (rows ?? []).find((r) => r.track_inventory !== false);
  if (!active) return { managed: false, available: 0 };
  const available = Math.max(0, Math.floor(Number(active.quantity_on_hand) || 0));
  return { managed: true, available: active.is_out_of_stock === true ? 0 : available };
}
